import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { getConnectedApplianceIds } from '../../Functions/ActuatorChannels';
import { getFromLocalStorage, saveToLocalStorage } from '../../Functions/LocalStorageUtils';
import { setShowActuatorDetail, setShowDeviceDetail } from '../../redux/reducers/appReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';

import styles from './Tutorial.module.scss';

const TUTORIAL_DONE_KEY = 'tutorialCompleted';
const SPOTLIGHT_PADDING = 8;

type ArrowDirection = 'top' | 'bottom' | 'left' | 'right' | null;
type ActionType = 'addAppliance' | 'openNewDevice' | 'addController' | 'openDeviceDetail' | 'connectAppliance' | 'addActuator' | 'openActuatorDetail' | 'connectActuator';
type TooltipAlign = 'auto' | 'top-right';

interface TutorialStep {
  titleKey: string;
  descriptionKey: string;
  targetId: string | null;
  /** Allow the user to interact with the highlighted element (removes backdrop block) */
  allowInteraction?: boolean;
  /** Auto-advance when this Redux condition is met */
  actionType?: ActionType;
  /** Override tooltip placement for null-target steps */
  tooltipAlign?: TooltipAlign;
  /** Optional secondary element to highlight alongside the primary target */
  secondaryTargetId?: string;
}

function getSpotlightRectMultiple(targetIds: string[]): SpotlightRect | null {
  const rects = targetIds
    .map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      };
    })
    .filter((r) => r !== null) as SpotlightRect[];

  if (rects.length === 0) return null;
  if (rects.length === 1) return rects[0];

  // Combine multiple rects
  const top = Math.min(...rects.map(r => r.top));
  const left = Math.min(...rects.map(r => r.left));
  const bottom = Math.max(...rects.map(r => r.top + r.height));
  const right = Math.max(...rects.map(r => r.left + r.width));

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  };
}

const STEPS: TutorialStep[] = [
  {
    titleKey: 'tutorial.steps.welcome.title',
    descriptionKey: 'tutorial.steps.welcome.description',
    targetId: null,
  },
  {
    titleKey: 'tutorial.steps.topbar.title',
    descriptionKey: 'tutorial.steps.topbar.description',
    targetId: 'tutorial-topbar',
  },
  {
    titleKey: 'tutorial.steps.leftMenu.title',
    descriptionKey: 'tutorial.steps.leftMenu.description',
    targetId: 'tutorial-left-menu',
    allowInteraction: true,
    secondaryTargetId: 'tutorial-bottom-menu',
  },
  {
    titleKey: 'tutorial.steps.addAppliance.title',
    descriptionKey: 'tutorial.steps.addAppliance.description',
    targetId: 'tutorial-floorplan',
    allowInteraction: true,
    actionType: 'addAppliance',
  },
  {
    titleKey: 'tutorial.steps.rightMenu.title',
    descriptionKey: 'tutorial.steps.rightMenu.description',
    targetId: 'tutorial-right-menu',
    allowInteraction: true,
    secondaryTargetId: 'tutorial-bottom-menu',
  },
  {
    titleKey: 'tutorial.steps.dinRail.title',
    descriptionKey: 'tutorial.steps.dinRail.description',
    targetId: 'tutorial-bottom-menu',
    allowInteraction: true,
  },
  {
    titleKey: 'tutorial.steps.addActuator.title',
    descriptionKey: 'tutorial.steps.addActuator.description',
    targetId: 'tutorial-left-menu',
    allowInteraction: true,
    actionType: 'addActuator',
  },
  {
    titleKey: 'tutorial.steps.openActuatorDetail.title',
    descriptionKey: 'tutorial.steps.openActuatorDetail.description',
    targetId: 'tutorial-floorplan',
    allowInteraction: true,
    actionType: 'openActuatorDetail',
  },
  {
    titleKey: 'tutorial.steps.connectActuator.title',
    descriptionKey: 'tutorial.steps.connectActuator.description',
    targetId: null,
    allowInteraction: true,
    tooltipAlign: 'top-right',
    actionType: 'connectActuator',
  },
  {
    titleKey: 'tutorial.steps.addController.title',
    descriptionKey: 'tutorial.steps.addController.description',
    targetId: 'tutorial-left-menu',
    allowInteraction: true,
    actionType: 'openNewDevice',
  },
  {
    titleKey: 'tutorial.steps.configureController.title',
    descriptionKey: 'tutorial.steps.configureController.description',
    targetId: 'tutorial-new-device',
    allowInteraction: true,
    tooltipAlign: 'top-right',
    actionType: 'addController',
  },
  {
    titleKey: 'tutorial.steps.openDeviceDetail.title',
    descriptionKey: 'tutorial.steps.openDeviceDetail.description',
    targetId: 'tutorial-floorplan',
    allowInteraction: true,
    actionType: 'openDeviceDetail',
  },
  {
    titleKey: 'tutorial.steps.connectAppliance.title',
    descriptionKey: 'tutorial.steps.connectAppliance.description',
    targetId: null,
    allowInteraction: true,
    tooltipAlign: 'top-right',
    actionType: 'connectAppliance',
  },
  {
    titleKey: 'tutorial.steps.save.title',
    descriptionKey: 'tutorial.steps.save.description',
    targetId: 'tutorial-left-menu',
  },
  {
    titleKey: 'tutorial.steps.settings.title',
    descriptionKey: 'tutorial.steps.settings.description',
    targetId: 'tutorial-left-menu',
  },
];

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrow: ArrowDirection;
}

function getSpotlightRect(targetId: string): SpotlightRect | null {
  const el = document.getElementById(targetId);
  if (!el) {
    console.error(`Element with ID '${targetId}' not found in the DOM.`);
    return null;
  }
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };
}

const TOOLTIP_WIDTH = 380;
const TOOLTIP_HEIGHT_ESTIMATE = 260;

function computeTooltipPosition(
  spotlight: SpotlightRect | null,
  viewportW: number,
  viewportH: number,
  tooltipAlign?: TooltipAlign,
): TooltipPosition {
  const GAP = 16;

  // Explicit alignment overrides automatic target-relative placement.
  if (tooltipAlign === 'top-right') {
    return {
      top: GAP,
      left: viewportW - TOOLTIP_WIDTH - GAP,
      arrow: null,
    };
  }

  if (!spotlight) {
    return {
      top: viewportH / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2,
      left: viewportW / 2 - TOOLTIP_WIDTH / 2,
      arrow: null,
    };
  }

  const { top, left, width, height } = spotlight;
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  const spaceBelow = viewportH - (top + height);
  const spaceAbove = top;
  const spaceRight = viewportW - (left + width);
  const spaceLeft = left;

  if (spaceBelow >= TOOLTIP_HEIGHT_ESTIMATE + GAP) {
    return {
      top: top + height + GAP,
      left: Math.min(Math.max(centerX - TOOLTIP_WIDTH / 2, GAP), viewportW - TOOLTIP_WIDTH - GAP),
      arrow: 'top',
    };
  }
  if (spaceAbove >= TOOLTIP_HEIGHT_ESTIMATE + GAP) {
    return {
      top: top - TOOLTIP_HEIGHT_ESTIMATE - GAP,
      left: Math.min(Math.max(centerX - TOOLTIP_WIDTH / 2, GAP), viewportW - TOOLTIP_WIDTH - GAP),
      arrow: 'bottom',
    };
  }
  if (spaceRight >= TOOLTIP_WIDTH + GAP) {
    return {
      top: Math.min(Math.max(centerY - TOOLTIP_HEIGHT_ESTIMATE / 2, GAP), viewportH - TOOLTIP_HEIGHT_ESTIMATE - GAP),
      left: left + width + GAP,
      arrow: 'left',
    };
  }
  if (spaceLeft >= TOOLTIP_WIDTH + GAP) {
    return {
      top: Math.min(Math.max(centerY - TOOLTIP_HEIGHT_ESTIMATE / 2, GAP), viewportH - TOOLTIP_HEIGHT_ESTIMATE - GAP),
      left: left - TOOLTIP_WIDTH - GAP,
      arrow: 'right',
    };
  }

  return {
    top: viewportH / 2 - TOOLTIP_HEIGHT_ESTIMATE / 2,
    left: viewportW / 2 - TOOLTIP_WIDTH / 2,
    arrow: null,
  };
}

const Tutorial: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [actionDone, setActionDone] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0, arrow: null });
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionBaselineRef = useRef(0);
  const actuatorBaselineRef = useRef(0);
  const actuatorConnectionBaselineRef = useRef(0);
  const connectedSlotsCountRef = useRef(0);
  const actuatorsCountRef = useRef(0);
  const connectedAppliancesInActuatorsRef = useRef(0);

  const appliances = useAppSelector((state) => state.app.appliances);
  const controllers = useAppSelector((state) => state.controllers.devices);
  const actuators = useAppSelector((state) => state.app.actuators);
  const selectingDevice = useAppSelector((state) => state.app.selectingDevice);
  const showDeviceDetail = useAppSelector((state) => state.app.showDeviceDetail);
  const showActuatorDetail = useAppSelector((state) => state.app.showActuatorDetail);
  const connectedSlotsCount = useAppSelector((state) =>
    state.controllers.devices.reduce((sum, controller) => {
      const connectedSlots = controller.iconsData.filter((slot) => (slot.applianceId ?? []).length > 0).length;
      return sum + connectedSlots;
    }, 0)
  );
  const connectedAppliancesInActuators = useAppSelector((state) =>
    state.app.actuators.reduce((sum, actuator) => sum + getConnectedApplianceIds(actuator).length, 0)
  );

  useEffect(() => {
    connectedSlotsCountRef.current = connectedSlotsCount;
  }, [connectedSlotsCount]);

  useEffect(() => {
    actuatorsCountRef.current = actuators.length;
  }, [actuators.length]);

  useEffect(() => {
    connectedAppliancesInActuatorsRef.current = connectedAppliancesInActuators;
  }, [connectedAppliancesInActuators]);

  const step = STEPS[stepIndex];

  const updatePositions = useCallback(() => {
    let rect: SpotlightRect | null = null;
    if (step.targetId && step.secondaryTargetId) {
      rect = getSpotlightRectMultiple([step.targetId, step.secondaryTargetId]);
    } else if (step.targetId) {
      rect = getSpotlightRect(step.targetId);
    }
    setSpotlightRect(rect);
    setTooltipPos(computeTooltipPosition(rect, window.innerWidth, window.innerHeight, step.tooltipAlign));

    // Retry if target not in DOM yet (e.g. DeviceDetail just mounted)
    if (step.targetId && !rect) {
      retryTimerRef.current = setTimeout(updatePositions, 150);
    }
  }, [step.targetId, step.secondaryTargetId, step.tooltipAlign]);

  // Show tutorial only on first visit
  useEffect(() => {
    const done = getFromLocalStorage(TUTORIAL_DONE_KEY) === 'true' ? true : false;
    const tech = getFromLocalStorage('tech') || null;
    if (!done && tech !== null) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Update spotlight when step changes or window resizes
  useEffect(() => {
    if (!visible) return;
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    setActionDone(false);

    if (step.actionType === 'connectAppliance') {
      connectionBaselineRef.current = connectedSlotsCountRef.current;
    }
    if (step.actionType === 'addActuator') {
      actuatorBaselineRef.current = actuatorsCountRef.current;
    }
    if (step.actionType === 'connectActuator') {
      actuatorConnectionBaselineRef.current = connectedAppliancesInActuatorsRef.current;
    }

    updatePositions();

    const handleResize = () => updatePositions();
    window.addEventListener('resize', handleResize);

    if (step.targetId) {
      const el = document.getElementById(step.targetId);
      if (el) {
        resizeObserverRef.current = new ResizeObserver(updatePositions);
        resizeObserverRef.current.observe(el);
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserverRef.current?.disconnect();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [visible, updatePositions, step.targetId, stepIndex, step.actionType]);

  // Auto-advance on action steps when the Redux condition is met
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!visible || !step.actionType || actionDone) return;

    let conditionMet = false;

    if (step.actionType === 'addAppliance' && appliances.length > 0) conditionMet = true;
    if (step.actionType === 'openNewDevice' && selectingDevice) conditionMet = true;
    if (step.actionType === 'addController' && controllers.length > 0) conditionMet = true;
    if (step.actionType === 'openDeviceDetail' && showDeviceDetail) conditionMet = true;
    if (step.actionType === 'connectAppliance' && connectedSlotsCount > connectionBaselineRef.current) conditionMet = true;
    if (step.actionType === 'connectActuator' && connectedAppliancesInActuators > actuatorConnectionBaselineRef.current) conditionMet = true;
    if (step.actionType === 'addActuator' && actuators.length > actuatorBaselineRef.current) conditionMet = true;
    if (step.actionType === 'openActuatorDetail' && showActuatorDetail) conditionMet = true;

    if (conditionMet) {
      setActionDone(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step.actionType, stepIndex, appliances.length, selectingDevice, controllers.length, showDeviceDetail, connectedSlotsCount, actuators, showActuatorDetail, connectedAppliancesInActuators, dispatch]);

  useEffect(() => {
    if (!visible || !actionDone) return;

    const timer = setTimeout(() => {
      if (step.actionType === 'connectAppliance') {
        dispatch(setShowDeviceDetail(false));
      }
      if (step.actionType === 'connectActuator') {
        dispatch(setShowActuatorDetail(false));
      }

      if (stepIndex < STEPS.length - 1) {
        setStepIndex((prev) => prev + 1);
      } else {
        finish();
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [visible, actionDone, step.actionType, stepIndex, dispatch]);

  const handleNext = () => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      finish();
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const finish = () => {
    saveToLocalStorage(TUTORIAL_DONE_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const isLast = stepIndex === STEPS.length - 1;
  const isFirst = stepIndex === 0;
  const isActionStep = !!step.actionType;
  const isInteractive = !!step.allowInteraction;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={t('tutorial.ariaLabel')}>
      {/* Backdrop — blocks clicks unless this is an interactive step */}
      <div className={classNames(styles.backdrop, { [styles.backdrop__passthrough]: isInteractive })} />

      {/* Spotlight highlight */}
      {spotlightRect && (
        <div
          className={styles.spotlight}
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className={styles.tooltip}
        style={{ top: tooltipPos.top, left: tooltipPos.left, width: TOOLTIP_WIDTH }}
      >
        {tooltipPos.arrow && (
          <div className={classNames(styles.arrow, styles[`arrow--${tooltipPos.arrow}`])} />
        )}

        <div className={styles.tooltip__step}>
          {t('tutorial.stepOf', { current: stepIndex + 1, total: STEPS.length })}
        </div>
        <div className={styles.tooltip__title}>{t(step.titleKey)}</div>
        <div className={styles.tooltip__description}>{t(step.descriptionKey)}</div>

        {/* Action waiting indicator */}
        {isActionStep && !actionDone && (
          <div className={styles.actionWaiting}>
            <div className={styles.actionWaiting__dots}>
              <span /><span /><span />
            </div>
            <span className={styles.actionWaiting__label}>{t('tutorial.waitingForAction')}</span>
          </div>
        )}
        {isActionStep && actionDone && (
          <div className={styles.actionDone}>
            <span className={styles.actionDone__icon}>✓</span>
            <span>{t('tutorial.actionDone')}</span>
          </div>
        )}

        <div className={styles.tooltip__actions}>
          <div className={styles.tooltip__dots}>
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={classNames(styles.tooltip__dot, {
                  [styles['tooltip__dot--active']]: i === stepIndex,
                })}
              />
            ))}
          </div>
          <div className={styles.tooltip__buttons}>
            {!isFirst && (
              <button className={styles.btnSkip} onClick={handlePrev}>
                {t('tutorial.prev')}
              </button>
            )}
            {isFirst && (
              <button className={styles.btnSkip} onClick={finish}>
                {t('tutorial.skip')}
              </button>
            )}
            {isActionStep && !actionDone ? (
              <button className={styles.btnSkip} onClick={handleNext}>
                {t('tutorial.skipAction')}
              </button>
            ) : (
              <button className={styles.btnNext} onClick={handleNext}>
                {isLast ? t('tutorial.finish') : t('tutorial.next')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
